import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui-kit";
import { RotateCcw, ScanBarcode, Store } from "lucide-react";

/** Quantas compras aparecem antes do "ver todas". */
const VISIVEIS = 3;

/** Data curta: o ano só entra quando não é o corrente — no balcão ele é ruído. */
function dt(iso) {
  const d = new Date(iso);
  const curto = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

  return d.getFullYear() === new Date().getFullYear() ? curto : `${curto}/${String(d.getFullYear()).slice(-2)}`;
}

const descreve = (i) =>
  `${i.qty}× ${i.name}${i.variant_label && i.variant_label !== "Único" ? ` (${i.variant_label})` : ""}`;

/**
 * O que este cliente já levou daqui — dentro do PDV, assim que ele é vinculado.
 *
 * Identificar o cliente no balcão só servia para a nota e para a comissão: a
 * informação que o vendedor precisa na hora — "qual era mesmo a bengala que a
 * senhora comprou da última vez?" — estava a duas telas de distância, no CRM, e
 * quem atende não abandona o balcão com o cliente na frente para ir buscá-la.
 * Agora ela chega junto do vínculo, e o botão Repetir traz a compra inteira
 * para a venda em curso.
 *
 * Vem do MESMO endpoint do CRM (balcão + loja online na mesma lista), só que
 * pela porta do PDV: quem atende costuma não ter a tela de CRM no perfil.
 */
export default function CustomerHistory({ customer, onRepeat }) {
  const [state, setState] = useState({ loading: true, data: [], summary: null });
  const [repeating, setRepeating] = useState(null);
  const [todas, setTodas] = useState(false);

  useEffect(() => {
    let vivo = true;
    setState({ loading: true, data: [], summary: null });
    setTodas(false);
    api.get(`/pos/customers/${customer.id}/purchases`)
      .then((r) => vivo && setState({ loading: false, data: r.data?.data || [], summary: r.data?.summary || null }))
      // Histórico é apoio, não a venda: falha de rede aqui fica quieta em vez de
      // virar um erro vermelho no meio do atendimento.
      .catch(() => vivo && setState({ loading: false, data: [], summary: null }));

    return () => { vivo = false; };
  }, [customer.id]);

  /**
   * Repetir: o servidor remonta os itens com preço e saldo de HOJE.
   *
   * A reconferência é dele de propósito — copiar o preço da compra antiga faria
   * o vendedor cobrar um valor que não existe mais e prometer peça que já foi
   * vendida. O PDV recebe os itens já conferidos e avisa o que mudou.
   */
  const refazer = async (compra) => {
    if (repeating) return;
    setRepeating(compra.id);
    try {
      const { data } = await api.get(`/pos/customers/${customer.id}/purchases/${compra.id}/repeat`);
      onRepeat(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRepeating(null);
    }
  };

  if (state.loading) {
    return <p className="px-1 text-xs text-slate-400" data-testid="pos-history-loading">Buscando compras anteriores…</p>;
  }

  // Compra cancelada fica fora: no CRM ela é registro, aqui seria oferta — e
  // oferecer de volta o que o cliente devolveu é o oposto de ajudar a vender.
  const compras = state.data.filter((c) => !c.cancelled);
  if (!compras.length) {
    return <p className="px-1 text-xs text-slate-400" data-testid="pos-history-empty">Primeira compra deste cliente.</p>;
  }

  const lista = todas ? compras : compras.slice(0, VISIVEIS);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70" data-testid="pos-customer-history">
      <p className="flex items-baseline justify-between gap-2 px-3 pt-2 pb-1">
        <span className="text-xs font-semibold text-slate-700">Já levou daqui</span>
        {state.summary && (
          <span className="text-xs text-slate-500">
            {state.summary.count} {state.summary.count === 1 ? "compra" : "compras"} ·{" "}
            <span className="font-mono">{brl(state.summary.total)}</span>
          </span>
        )}
      </p>

      <ul className="divide-y divide-slate-200/70">
        {lista.map((c) => (
          <li key={c.id} className="flex items-start gap-2 px-3 py-2" data-testid="pos-history-row">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                {/* O canal muda o que dá para prometer (a loja tem frete, o
                    balcão não), então continua visível também aqui. */}
                {c.origin === "balcao"
                  ? <ScanBarcode size={11} className="shrink-0" aria-hidden />
                  : <Store size={11} className="shrink-0" aria-hidden />}
                {dt(c.at)} · <span className="font-mono text-slate-700">{brl(c.total)}</span>
              </p>
              <p className="mt-0.5 text-xs text-slate-700 line-clamp-2">
                {c.items.map(descreve).join(" · ") || "—"}
              </p>
            </div>
            <Button variant="ghost" size="sm" icon={RotateCcw} className="shrink-0"
              loading={repeating === c.id} onClick={() => refazer(c)}
              aria-label={`Repetir a compra ${c.number}`} data-testid="pos-history-repeat">
              Repetir
            </Button>
          </li>
        ))}
      </ul>

      {compras.length > VISIVEIS && !todas && (
        <Button variant="ghost" size="sm" block onClick={() => setTodas(true)}
          className="rounded-t-none text-brand-700" data-testid="pos-history-more">
          Ver as {compras.length} compras
        </Button>
      )}
    </div>
  );
}
